/*
 * Form styles partagées — source de vérité unique pour la hauteur, les
 * couleurs et les états (hover/focus) de TOUS les champs de saisie de
 * l'app : <input>, <textarea>, <select> natif, Combobox shadcn.
 *
 * Pourquoi : avant cette extraction, chaque écran réinventait son propre
 * `inputClass` (Patient/, Login/, Profile/, etc.) avec des `py-2.5` /
 * `py-3` / `h-10` mélangés. Côte à côte, deux champs pouvaient afficher
 * 40, 42, 44 ou 48 px — visuellement non aligné. La modale "Ajouter un
 * patient" utilisait `<select>` natif (≈42 px) tandis que les Tabs
 * Consultation utilisaient `Combobox` (h-10 = 40 px), même formulaire,
 * deux hauteurs.
 *
 * Décision (2026-05-05) : baseline `h-11` (44 px) PARTOUT, via height
 * explicite plutôt que padding — cohérent quel que soit le composant
 * sous-jacent (Radix Button, <input>, <select>). Voir CLAUDE.md
 * §Composants — input pour le contexte design system.
 */

/**
 * Champs <input> texte/number/email/tel et <textarea>. Hauteur fixe
 * `h-11` (44 px) — ne **PAS** ajouter de `py-*` ici, c'est `h-11` qui
 * fixe la hauteur ; `flex items-center` n'est pas nécessaire car les
 * inputs natifs centrent verticalement leur valeur tout seuls.
 *
 * `mt-1` est inclus par défaut car la quasi-totalité des callsites suit
 * le pattern `<label><span>Label</span><input className={inputClass}/></label>`
 * et a besoin de respiration entre la légende et le champ. Pour annuler
 * dans un contexte sans label (ex. cellules de table), passer `mt-0`
 * via `cn(inputClass, "mt-0")` — twMerge dédoublonne.
 */
export const inputClass =
  "block w-full h-11 mt-1 rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-700 placeholder-slate-400 transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

/**
 * <select> natif HTML. Variante {@link inputClass} avec :
 *   - `appearance-none` pour neutraliser la flèche système (chaque
 *     navigateur a la sienne, on rend la nôtre via un Boxicon absolu).
 *   - `cursor-pointer` parce que c'est un bouton interactif, pas un
 *     champ texte libre.
 *   - `pr-9` pour réserver la place du chevron à droite (32 px).
 *
 * Utilisation type :
 *   <div className="relative">
 *     <select className={selectClass}>...</select>
 *     <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
 *   </div>
 */
export const selectClass =
  inputClass + " appearance-none cursor-pointer pr-9";

/**
 * Trigger du Combobox shadcn (cf. `@/components/ui/combobox`). Identique
 * visuellement à `inputClass` mais avec :
 *   - `justify-between` car le trigger contient le label sélectionné +
 *     un chevron à droite.
 *   - `text-left` car les <button> sont center par défaut, contrairement
 *     aux <input> qui s'alignent à gauche.
 *
 * NOTE : le Combobox interne utilise `cn(comboboxTriggerClass, className)`
 * — toute classe passée par le consommateur (ex. `w-full`) s'ajoute, et
 * tailwind-merge supprime les conflits.
 */
export const comboboxTriggerClass =
  "flex w-full h-11 items-center justify-between rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-700 text-left transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Variante {@link comboboxTriggerClass} pour MultiCombobox : la hauteur
 * peut grandir avec les badges sélectionnés. `min-h-11` au lieu de `h-11`,
 * + `py-1.5` pour le padding vertical des badges quand on a plusieurs
 * lignes.
 */
export const multiComboboxTriggerClass =
  "flex w-full min-h-11 items-center justify-between rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm text-slate-700 text-left transition-all duration-200 hover:border-slate-300 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-500/10 disabled:cursor-not-allowed disabled:opacity-50";
